import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type {
  Organization,
  OrganizationRole,
  PlatformRole,
  SessionUser,
} from "@compliance-sage/contracts";
import { api } from "../api/client";
import { clearMaterialCache } from "../features/reviews/materialCache";

interface AuthContextValue {
  user: SessionUser | null;
  platformRole: PlatformRole | null;
  availableOrganizations: Organization[];
  activeOrganization: Organization | null;
  loading: boolean;
  switchingOrganization: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  switchOrganization: (organizationId: string) => Promise<void>;
  can: (perm: Perm) => boolean;
}

// Mirror of the server permission matrix (frontend convenience only; the API
// is the real authority).
export type Perm =
  | "ORGANIZATION_MANAGE"
  | "MEMBER_VIEW"
  | "POLICY_VIEW"
  | "POLICY_MANAGE"
  | "RULE_VIEW"
  | "RULE_MANAGE"
  | "CONFIG_MANAGE"
  | "AI_QUALITY_VIEW"
  | "REVIEW_CREATE"
  | "REVIEW_VIEW_ALL"
  | "REVIEW_DECIDE"
  | "REVIEW_DELETE"
  | "AUDIT_VIEW"
  | "MEMBER_INVITE_ADMIN"
  | "MEMBER_DELETE"
  | "SUPPORT_CREATE"
  | "SUPPORT_VIEW_ORG"
  | "ASK_SAGE_USE"
  | "ASK_SAGE_VIEW_ORG";

const PERMS: Record<OrganizationRole, Perm[]> = {
  USER: ["POLICY_VIEW", "RULE_VIEW", "REVIEW_CREATE", "SUPPORT_CREATE", "ASK_SAGE_USE"],
  ADMIN: [
    "MEMBER_VIEW",
    "MEMBER_INVITE_ADMIN",
    "MEMBER_DELETE",
    "POLICY_VIEW",
    "POLICY_MANAGE",
    "RULE_VIEW",
    "RULE_MANAGE",
    "CONFIG_MANAGE",
    "AI_QUALITY_VIEW",
    "REVIEW_CREATE",
    "REVIEW_VIEW_ALL",
    "REVIEW_DECIDE",
    "REVIEW_DELETE",
    "AUDIT_VIEW",
    "SUPPORT_CREATE",
    "SUPPORT_VIEW_ORG",
    "ASK_SAGE_USE",
    "ASK_SAGE_VIEW_ORG",
  ],
};

const PLATFORM_PERMS: Perm[] = [
  "ORGANIZATION_MANAGE",
  "MEMBER_VIEW",
  "POLICY_VIEW",
  "POLICY_MANAGE",
  "RULE_VIEW",
  "RULE_MANAGE",
  "CONFIG_MANAGE",
  "AI_QUALITY_VIEW",
  "REVIEW_CREATE",
  "REVIEW_VIEW_ALL",
  "REVIEW_DECIDE",
  "REVIEW_DELETE",
  "AUDIT_VIEW",
  "MEMBER_INVITE_ADMIN",
  "MEMBER_DELETE",
  "SUPPORT_CREATE",
  "SUPPORT_VIEW_ORG",
  "ASK_SAGE_USE",
  "ASK_SAGE_VIEW_ORG",
];

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [availableOrganizations, setAvailableOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [switchingOrganization, setSwitchingOrganization] = useState(false);

  const refresh = useCallback(async () => {
    // Both calls share the same session cookie, so there's no need for one to
    // wait on the other — firing them together instead of in a waterfall cuts
    // a full round trip off every first load.
    const [meResult, organizationsResult] = await Promise.allSettled([
      api.me(),
      api.listOrganizations(),
    ]);
    if (meResult.status === "fulfilled") {
      setUser(meResult.value);
      setAvailableOrganizations(organizationsResult.status === "fulfilled" ? organizationsResult.value : []);
    } else {
      setUser(null);
      setAvailableOrganizations([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async () => {
    const { authorizeUrl } = await api.startLogin();
    window.location.href = authorizeUrl;
  }, []);

  const logout = useCallback(async () => {
    await api.logout().catch(() => undefined);
    // Previewed review files must not outlive the session on a shared computer.
    await clearMaterialCache();
    setUser(null);
    setAvailableOrganizations([]);
    window.location.href = "/login";
  }, []);

  const activeOrganization = useMemo<Organization | null>(() => {
    if (!user?.organizationId) return null;
    return (
      availableOrganizations.find((organization) => organization.id === user.organizationId) ?? {
        id: user.organizationId,
        name: user.organizationName ?? user.organizationId,
        role: user.role ?? "USER",
        membershipStatus: null,
      }
    );
  }, [availableOrganizations, user]);

  const switchOrganization = useCallback(
    async (organizationId: string) => {
      if (organizationId === user?.organizationId) return;
      setSwitchingOrganization(true);
      try {
        await api.selectOrganization({ organizationId });
        await refresh();
      } finally {
        setSwitchingOrganization(false);
      }
    },
    [refresh, user?.organizationId],
  );

  const can = useCallback(
    (perm: Perm) => {
      if (!user) return false;
      if (user.platformRole === "SUPER_ADMIN") {
        return perm === "ORGANIZATION_MANAGE" || Boolean(user.organizationId && PLATFORM_PERMS.includes(perm));
      }
      if (perm === "ORGANIZATION_MANAGE" || !user.organizationId || !user.role) return false;
      return user.role !== "SUPER_ADMIN" && PERMS[user.role].includes(perm);
    },
    [user],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        platformRole: user?.platformRole ?? null,
        availableOrganizations,
        activeOrganization,
        loading,
        switchingOrganization,
        login,
        logout,
        refresh,
        switchOrganization,
        can,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
